import { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBoxRounded } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { Modal, Table } from '../../../components';
import { formatDate } from '../../../helpers';
import { getSalesToolsContractHistory, updateSalesToolsContractTemplate } from '../../../fetch';
import { ISalesToolsContractTemplate } from '../../../models';
import { useSnackbar } from 'notistack';

export const ContractManagementHistoryModal = ({
  handleClose,
  buildDocumentTypeId,
  fetchSalesToolsContractTemplates
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: templateHistory,
    isLoading: isLoadingContractHistory,
    refetch: fetchSalesToolsContractHistory
  } = useQuery(
    ['getSalesToolsContractHistory-contract-management', buildDocumentTypeId],
    () => getSalesToolsContractHistory(buildDocumentTypeId),
    {
      enabled: !!buildDocumentTypeId
    }
  );

  return (
    <Modal open title="File History" maxWidth="md" onClose={() => handleClose()}>
      <Box>
        <Table
          columns={[
            {
              Header: 'Active',
              accessor: 'isActive',
              sort: false,
              Cell: ({
                cell: {
                  row: { original }
                }
              }: {
                cell: { row: { original: ISalesToolsContractTemplate } };
              }) => {
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    {original?.isActive ? <CheckBoxRounded color="primary" /> : ''}
                    {!original?.isActive && (
                      <Tooltip
                        title={
                          original.isDeletedInPandaDoc
                            ? 'Template is deleted in Pandadoc'
                            : 'Set as "Active"'
                        }
                      >
                        <span>
                          <IconButton
                            sx={{ padding: 0 }}
                            size="small"
                            disabled={original?.isDeletedInPandaDoc}
                            onClick={async () => {
                              try {
                                setIsUpdating(true);
                                await updateSalesToolsContractTemplate(buildDocumentTypeId, {
                                  activePandaDocTemplateId: original.pandaDocTemplateId
                                });
                                fetchSalesToolsContractHistory();
                                fetchSalesToolsContractTemplates();
                                enqueueSnackbar(`File updated!`, {
                                  variant: 'success'
                                });
                              } catch (error: any) {
                                enqueueSnackbar(error?.Detail ?? `Error updating file.`, {
                                  variant: 'error'
                                });
                              } finally {
                                setIsUpdating(false);
                              }
                            }}
                            color="primary"
                          >
                            <CheckBoxOutlineBlank />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              }
            },
            {
              Header: 'File Name',
              accessor: 'documentName',
              sort: false,
              Cell: ({
                cell: {
                  row: { original }
                }
              }: {
                cell: { row: { original: ISalesToolsContractTemplate; index: number } };
              }) => {
                const displayName = original?.documentName;
                return <Typography>{displayName}</Typography>;
              }
            },
            {
              Header: 'Created Date',
              accessor: 'createdDate',
              sort: false,
              Cell: ({
                cell: {
                  row: { original }
                }
              }: {
                cell: { row: { original: ISalesToolsContractTemplate } };
              }) => {
                const formattedDate = formatDate(original?.createdDate);
                return <div>{formattedDate}</div>;
              }
            }
          ]}
          data={templateHistory ?? []}
          isLoading={isUpdating || isLoadingContractHistory}
          noResultsText="No file history found"
          hidePagination
        />
      </Box>
    </Modal>
  );
};
